import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import type { BotInstance, TradeHistory, CapitalTransaction, CandleResponse, PaginatedCommands } from '@/types/api';
import { setDeployStatus } from '@/stores/deploy-status';

export function useBots(opts?: { refetchInterval?: number }) {
  return useQuery<BotInstance[]>({
    queryKey: ['bots'],
    queryFn: async () => {
      const res = await api.get('/bots');
      return res.data;
    },
    refetchInterval: opts?.refetchInterval,
  });
}

export function useBot(id: string, opts?: { refetchInterval?: number }) {
  return useQuery<BotInstance>({
    queryKey: ['bots', id],
    queryFn: async () => {
      const res = await api.get(`/bots/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: opts?.refetchInterval,
  });
}

export interface OpenPosition {
  mt5_position_id: string;
  type: 'BUY' | 'SELL';
  symbol: string;
  volume: number;
  open_price: number;
  current_price?: number;
  floating_pnl?: number;
  opened_at: string;
}

export function useBotOpenPositions(id: string, opts?: { refetchInterval?: number }) {
  return useQuery<OpenPosition[]>({
    queryKey: ['bots', id, 'open-positions'],
    queryFn: async () => {
      const res = await api.get(`/bots/${id}/open-positions`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: opts?.refetchInterval,
  });
}

export interface TradeHistoryParams {
  page?: number;
  limit?: number;
  type?: 'BUY' | 'SELL';
  status?: 'OPEN' | 'CLOSED';
  refetchInterval?: number;
}

export interface PaginatedTrades {
  trades: TradeHistory[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useBotTrades(id: string, params?: TradeHistoryParams) {
  const { refetchInterval, ...queryParams } = params ?? {};
  return useQuery<PaginatedTrades>({
    queryKey: ['bots', id, 'trades', queryParams],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (queryParams.page) searchParams.set('page', String(queryParams.page));
      if (queryParams.limit) searchParams.set('limit', String(queryParams.limit));
      if (queryParams.type) searchParams.set('type', queryParams.type);
      if (queryParams.status) searchParams.set('status', queryParams.status);
      const qs = searchParams.toString();
      const res = await api.get(`/bots/${id}/trades${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval,
  });
}

interface PollOptions {
  timeoutMs?: number;
  onStatus?: (status: string) => void;
}

async function pollCommandUntilDone(commandId: string, opts: PollOptions = {}): Promise<any> {
  const { timeoutMs = 120_000, onStatus } = opts;
  const start = Date.now();
  let lastStatus = '';
  while (Date.now() - start < timeoutMs) {
    const res = await api.get(`/bots/commands/${commandId}`);
    const cmd = res.data;
    if (cmd.status !== lastStatus) {
      lastStatus = cmd.status;
      onStatus?.(cmd.status);
    }
    if (cmd.status === 'DONE') return cmd;
    if (cmd.status === 'FAILED') throw new Error(cmd.error || 'Command failed');
    await new Promise((r) => setTimeout(r, 2_000));
  }
  throw new Error('This is taking longer than usual. Your bot may still be starting — refresh the page to check its status.');
}

export function useStartBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      setDeployStatus('PENDING', 'DEPLOYING BOT', 'Preparing your bot...');
      const res = await api.post(`/bots/${id}/start`);
      const data = res.data;
      if (data?.commandId) {
        return pollCommandUntilDone(data.commandId, {
          timeoutMs: 180_000,
          onStatus: (s) => {
            if (s === 'PENDING') setDeployStatus('PENDING', 'DEPLOYING BOT', 'Waiting for engine worker...');
            else if (s === 'PROCESSING') setDeployStatus('PROCESSING', 'DEPLOYING BOT', 'Connecting to broker...');
          },
        });
      }
      return data;
    },
    onSuccess: async (_data, id) => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['bots'] }),
        qc.refetchQueries({ queryKey: ['bots', id] }),
      ]).catch(() => {});
      setDeployStatus(null);
      toast.success('Bot started');
    },
    onSettled: () => {
      // Always clear overlay — even if refetch fails
      setDeployStatus(null);
    },
    onError: (err) => {
      setDeployStatus(null);
      toast.error((err as Error)?.message || 'Failed to start bot');
    },
  });
}

export function useStopBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      setDeployStatus('STOPPING', 'STOPPING BOT', 'Closing positions...');
      const res = await api.post(`/bots/${id}/stop`);
      const data = res.data;
      if (data?.commandId) {
        return pollCommandUntilDone(data.commandId, {
          onStatus: (s) => {
            if (s === 'PROCESSING') setDeployStatus('STOPPING', 'STOPPING BOT', 'Shutting down...');
          },
        });
      }
      return data;
    },
    onSuccess: async (_data, id) => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['bots'] }),
        qc.refetchQueries({ queryKey: ['bots', id] }),
      ]).catch(() => {});
      setDeployStatus(null);
      toast.success('Bot stopped');
    },
    onSettled: async (_data, _error, id) => {
      // Always clear overlay — even if refetch fails
      setDeployStatus(null);
      qc.invalidateQueries({ queryKey: ['bots'] });
      qc.invalidateQueries({ queryKey: ['bots', id] });
    },
    onError: (err) => {
      setDeployStatus(null);
      toast.error((err as Error)?.message || 'Failed to stop bot');
    },
  });
}

export function useDepositBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount }: { id: string; amount: number }) => {
      const res = await api.post(`/bots/${id}/deposit`, { amount });
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['bots'] });
      qc.invalidateQueries({ queryKey: ['bots', id] });
      qc.invalidateQueries({ queryKey: ['bots', id, 'transactions'] });
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useWithdrawBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount }: { id: string; amount: number }) => {
      const res = await api.post(`/bots/${id}/withdraw`, { amount });
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['bots'] });
      qc.invalidateQueries({ queryKey: ['bots', id] });
      qc.invalidateQueries({ queryKey: ['bots', id, 'transactions'] });
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useUpdateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: {
      id: string;
      sl_pct?: number | null;
      tp_pct?: number | null;
      allocation_mode?: 'FIXED_LOT' | 'FIXED_MULT' | 'EQUITY_RATIO' | null;
      alloc_fixed_lot?: number | null;
      alloc_multiplier?: number | null;
    }) => {
      const res = await api.patch(`/bots/${id}`, body);
      return res.data as BotInstance;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['bots'] });
      qc.invalidateQueries({ queryKey: ['bots', id] });
    },
  });
}

export function useArchiveBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/bots/${id}/archive`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bots'] });
      qc.invalidateQueries({ queryKey: ['bots-archived'] });
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useArchivedBots() {
  return useQuery<BotInstance[]>({
    queryKey: ['bots-archived'],
    queryFn: async () => {
      const res = await api.get('/bots/archived');
      return res.data;
    },
  });
}

export function useBotAllTrades(id: string) {
  return useQuery<PaginatedTrades>({
    queryKey: ['bots', id, 'trades', 'all-closed'],
    queryFn: async () => {
      const res = await api.get(`/bots/${id}/trades?limit=100&status=CLOSED`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useBotCandles(id: string, opts?: { timeframe?: string; days?: number }) {
  const tf = opts?.timeframe ?? '5m';
  const days = opts?.days ?? 1;
  return useQuery<CandleResponse>({
    queryKey: ['bots', id, 'candles', tf, days],
    queryFn: async () => {
      const res = await api.get(`/bots/${id}/candles?timeframe=${tf}&days=${days}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

/** Live candles from CandleBufferService — polls every 5s for real-time updates */
export function useBotLiveCandles(id: string, opts?: { timeframe?: string; enabled?: boolean }) {
  const tf = opts?.timeframe ?? '5m';
  const enabled = opts?.enabled !== false;
  return useQuery<CandleResponse>({
    queryKey: ['bots', id, 'candles', 'live', tf],
    queryFn: async () => {
      const res = await api.get(`/bots/${id}/candles/live?timeframe=${tf}`);
      return res.data;
    },
    enabled: !!id && enabled,
    refetchInterval: 5_000,
  });
}

export function useCreateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      mode?: 'LIVE' | 'PAPER';
      mt5AccountId?: string;
      templateId?: string;
      symbol?: string;
      assigned_capital: number;
      sl_pct?: number;
      tp_pct?: number;
      region?: string;
      allocation_mode?: 'FIXED_LOT' | 'FIXED_MULT';
      alloc_fixed_lot?: number;
      alloc_multiplier?: number;
    }) => {
      const res = await api.post('/bots', body);
      return res.data as BotInstance;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bots'] });
    },
  });
}

export interface PaginatedTransactions {
  transactions: CapitalTransaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useBotTransactions(id: string, params?: { page?: number; limit?: number }) {
  return useQuery<PaginatedTransactions>({
    queryKey: ['bots', id, 'transactions', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      const res = await api.get(`/bots/${id}/transactions${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useBotCommands(id: string, params?: { page?: number; limit?: number }) {
  return useQuery<PaginatedCommands>({
    queryKey: ['bots', id, 'commands', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      const res = await api.get(`/bots/${id}/commands${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!id,
  });
}
